import { LitElement, html, utils } from "@hh/global.js";
const { getCart, addToCart, updateCart, reRenderShopifySections } = utils;

class HHFreeSamples extends LitElement {
  static properties = {
    threshold: {
      type: Number,
    },
    active: {
      type: Boolean,
    },
    hasFreeSample: {
      type: Boolean,
      attribute: "data-has-free-samples",
    },
  };

  constructor() {
    super();
    this.items = JSON.parse(this.dataset.initialSamples).map((item) => +item);
    this.blazeSlider = null;
    this._classObserver = null;

    if (!!this.dataset.unavailable) {
      this.clearFreeSamples();
    }
  }

  get freeSampleTrigger() {
    return this.querySelector("#free-sample-selector__trigger");
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      "CART:TEMPLATE_UPDATE",
      this.reapplyWarningLogic.bind(this),
    );

    this.freeSampleTrigger?.addEventListener(
      "input",
      this.handleFreeSamples.bind(this),
    );
    this.handleSamples();
    this.applyInitialErrorState();
  }

  firstUpdated() {
    this.initSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      "CART:TEMPLATE_UPDATE",
      this.reapplyWarningLogic,
    );
    this.freeSampleTrigger?.removeEventListener(
      "input",
      this.handleFreeSamples,
    );
    if (this.blazeSlider) {
      this.blazeSlider.destroy();
      this.blazeSlider = null;
    }
    if (this._classObserver) {
      this._classObserver.disconnect();
      this._classObserver = null;
    }
  }

  /* ── BlazeSlider carousel ── */

  initSlider() {
    var sliderElement = this.querySelector(".blaze-slider");
    if (!sliderElement) return;

    var slides = sliderElement.querySelectorAll(".blaze-slide");
    if (slides.length <= 1) return;

    if (typeof window.BlazeSlider === "undefined") {
      var script = document.querySelector('script[src*="blaze-slider"]');
      if (script) {
        script.addEventListener("load", () => this.initSlider(), {
          once: true,
        });
      }
      return;
    }

    try {
      this.blazeSlider = new window.BlazeSlider(sliderElement, {
        all: {
          enableAutoplay: false,
          enablePagination: false,
          slidesToShow: 3,
          slidesToScroll: 1,
          transitionDuration: 300,
          transitionTimingFunction: "ease-in-out",
          loop: false,
          draggable: true,
          slideGap: "16px",
        },
      });

      this.setupNavigation();
      this.observeForDragSwipe(sliderElement);
      this.syncNavigationState();
    } catch (error) {
      console.error(
        "free-sample-selector: Error initializing BlazeSlider:",
        error,
      );
    }
  }

  isAtStart() {
    return this.blazeSlider && this.blazeSlider.stateIndex === 0;
  }

  isAtEnd() {
    if (!this.blazeSlider || !this.blazeSlider.states) return false;
    return this.blazeSlider.stateIndex >= this.blazeSlider.states.length - 1;
  }

  syncNavigationState() {
    var atStart = this.isAtStart();
    var atEnd = this.isAtEnd();
    this.querySelectorAll(".js-slide-arrow").forEach((button) => {
      var direction = button.getAttribute("data-direction");
      if (direction === "prev" && atStart) {
        button.setAttribute("disabled", "true");
      } else if (direction === "next" && atEnd) {
        button.setAttribute("disabled", "true");
      } else {
        button.removeAttribute("disabled");
      }
    });
  }

  observeForDragSwipe(sliderElement) {
    if (this._classObserver) {
      this._classObserver.disconnect();
    }
    this._classObserver = new MutationObserver(() => {
      this.syncNavigationState();
    });
    this._classObserver.observe(sliderElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  setupNavigation() {
    this.querySelectorAll(".js-slide-arrow").forEach((button) => {
      button.addEventListener("click", (event) => this.onArrowClicked(event));
    });
  }

  onArrowClicked(event) {
    if (!this.blazeSlider) return;
    var direction = event.currentTarget.getAttribute("data-direction");
    if (direction === "prev") {
      this.blazeSlider.prev();
    } else {
      this.blazeSlider.next();
    }
    this.syncNavigationState();
  }

  async handleSamples() {
    let autoDisabled =
      sessionStorage.getItem("Free_Samples_Disable_Auto") == "true";
    let userDoesntWantSamples = sessionStorage.getItem("freeSamples");

    if (
      this.active &&
      this.freeSampleTrigger &&
      userDoesntWantSamples === null &&
      !autoDisabled
    ) {
      this.freeSampleTrigger.checked = true;
      this.addFreeSamples();
    } else if (this.freeSampleTrigger) {
      if (this.hasFreeSample && this.active) {
        this.freeSampleTrigger.checked = true;
      } else {
        this.freeSampleTrigger.checked = false;
      }
    }
  }

  handleFreeSamples(event) {
    const label = this.querySelector(
      'label[for="free-sample-selector__trigger"]',
    );
    const warning = this.querySelector("#free-sample-warning");

    if (event.target.checked) {
      this.addFreeSamples();
      if (label) label.classList.remove("free-sample-selector__trigger--error");
      if (warning) warning.style.display = "none";
    } else {
      sessionStorage.setItem("Free_Samples_Disable_Auto", "true");
      this.clearFreeSamples(true);
      if (label) label.classList.add("free-sample-selector__trigger--error");
      if (warning) warning.style.display = "block";
    }
  }

  reapplyWarningLogic() {
    const warning = this.querySelector("#free-sample-warning");
    const checkbox = this.freeSampleTrigger;
    const label = this.querySelector(
      'label[for="free-sample-selector__trigger"]',
    );
    if (checkbox && label && warning) {
      if (!checkbox.checked) {
        label.classList.add("free-sample-selector__trigger--error");
        warning.style.display = "block";
      } else {
        label.classList.remove("free-sample-selector__trigger--error");
        warning.style.display = "none";
      }
    }
  }

  applyInitialErrorState() {
    const checkbox = this.freeSampleTrigger;
    const label = this.querySelector(
      'label[for="free-sample-selector__trigger"]',
    );
    const warning = this.querySelector("#free-sample-warning");

    // If checkbox is unchecked, show error
    if (checkbox && !checkbox.checked && label && warning) {
      label.classList.add("free-sample-selector__trigger--error");
      warning.style.display = "block";
    }
  }

  async addFreeSamples() {
    document.activeElement.blur();

    const cart = await getCart();
    const updatePayload = {};
    const samplesInCart = cart.items
      .filter((item) => item.properties?._free_sample)
      .map((item) => {
        updatePayload[item.key] = 0;

        return item.key;
      });

    if (samplesInCart.length > 0) {
      document.activeElement.blur();
      await updateCart(
        {
          updates: updatePayload,
        },
        `${this.inlineCartSectionId}`,
      );
    }
    const items = this.items.map((id) => ({
      id,
      quantity: 1,
      properties: {
        _free_sample: true,
        _free_sample_threshold: this.threshold,
      },
    }));

    if (items.length > 0) {
      await addToCart({
        items,
      });
    }
    sessionStorage.removeItem("freeSamples");
  }

  async clearFreeSamples(sessionUpdate = false) {
    const cart = await getCart();

    const updates = {};
    const freeSamples = cart.items
      .filter((item) => !!item.properties._free_sample)
      .map((item) => item.key);

    if (freeSamples.length > 0) {
      freeSamples.map((key) => (updates[key] = 0));

      document.activeElement.blur();
      const response = await updateCart({
        updates,
      });
      reRenderShopifySections(response, "[data-rerender]");
      document.querySelector("hh-mini-cart")?.checkCartStatus();
    }
    if (sessionUpdate) {
      sessionStorage.setItem("freeSamples", "false");
    }
  }

  render() {
    return html`<div>
      <slot name="body"></slot>
    </div> `;
  }
}

customElements.define("free-sample-selector", HHFreeSamples);
